// src/pages/Disclaimer.jsx
import React, { useEffect, useState } from "react";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const DISCLAIMER_COPY = {
  es: {
    title: "Descargo de responsabilidad",
    updated: "Última actualización: junio de 2026",
    intro: (
      <>
        Los contenidos, sistemas, metodologías, materiales, herramientas y
        recursos ofrecidos en <strong>ZEDRO Academy</strong> tienen fines
        formativos, informativos y de aplicación personal.
      </>
    ),
    paragraphs: [
      <>
        ZEDRO Academy es un ecosistema formativo digital orientado a acompañar
        procesos de orden, claridad, propósito, reflexión personal y aplicación
        práctica. Su contenido no debe interpretarse como asesoría profesional
        personalizada.
      </>,
    ],
    sections: [
      {
        title: "1. No constituye asesoría profesional",
        content: (
          <>
            Nada dentro de ZEDRO Academy constituye asesoría legal, médica,
            psicológica, terapéutica, financiera, contable, fiscal, pastoral o
            profesional personalizada. Si necesitas orientación específica sobre
            una situación personal, de salud, emocional, financiera, legal o de
            otra naturaleza, debes consultar a un profesional calificado.
          </>
        ),
      },
      {
        title: "2. Uso personal y responsabilidad del usuario",
        content: (
          <>
            El uso de los contenidos, ejercicios, reflexiones, herramientas,
            bitácoras, recursos o recomendaciones generales se realiza bajo la
            responsabilidad del usuario. Cada persona debe evaluar su contexto,
            sus límites, sus necesidades y sus circunstancias antes de aplicar
            cualquier contenido de la plataforma.
          </>
        ),
      },
      {
        title: "3. Ausencia de garantías de resultado",
        content: (
          <>
            ZEDRO Academy no garantiza resultados específicos, transformaciones
            personales determinadas, cambios espirituales, emocionales,
            relacionales, profesionales, financieros o de salud. Los resultados
            dependen de múltiples factores individuales, decisiones personales,
            constancia, contexto y circunstancias externas que ZEDRO Academy no
            controla.
          </>
        ),
      },
      {
        title: "4. Contenido formativo y espiritual",
        content: (
          <>
            Algunos contenidos pueden incluir principios de inspiración
            judeocristiana, reflexión espiritual, formación del carácter,
            propósito, mayordomía, relaciones y vida cotidiana. Estos contenidos
            son ofrecidos con fines formativos y no reemplazan acompañamiento
            pastoral, consejería profesional, atención clínica o intervención
            especializada cuando sea necesaria.
          </>
        ),
      },
      {
        title: "5. Salud física, mental y emocional",
        content: (
          <>
            La información relacionada con hábitos, bienestar, orden personal,
            emociones, descanso, alimentación, cuerpo, disciplina o vida diaria
            tiene carácter general y educativo. No debe utilizarse para
            diagnosticar, tratar, prevenir o sustituir atención médica,
            psicológica, psiquiátrica, nutricional o terapéutica profesional.
          </>
        ),
      },
      {
        title: "6. Disponibilidad de la plataforma",
        content: (
          <>
            Aunque ZEDRO Academy procura mantener la plataforma disponible,
            segura y funcional, no garantiza que el servicio esté libre de
            interrupciones, errores técnicos, fallas temporales, pérdida de
            acceso, mantenimiento, actualizaciones o eventos fuera de su control.
          </>
        ),
      },
      {
        title: "7. Servicios, enlaces e integraciones de terceros",
        content: (
          <>
            ZEDRO Academy puede utilizar o enlazar servicios de terceros, como
            Stripe para pagos, servicios de correo, herramientas de analítica,
            alojamiento, infraestructura tecnológica u otros proveedores. El uso
            de estos servicios puede estar sujeto a sus propios términos,
            políticas y prácticas. ZEDRO Academy no controla ni asume
            responsabilidad por plataformas externas.
          </>
        ),
      },
      {
        title: "8. Limitación de responsabilidad",
        content: (
          <>
            En la medida permitida por la ley aplicable, ZEDRO Academy no será
            responsable por daños indirectos, incidentales, consecuenciales,
            pérdida de datos, pérdida de oportunidades, interrupciones,
            decisiones personales tomadas por el usuario o resultados derivados
            del uso o imposibilidad de uso de la plataforma, salvo que la ley
            aplicable exija lo contrario.
          </>
        ),
      },
      {
        title: "9. Relación con otros documentos legales",
        content: (
          <>
            Este descargo complementa los{" "}
            <a href="/legal/terminos">Términos y Condiciones</a>, la{" "}
            <a href="/legal/privacidad">Política de Privacidad</a>, la{" "}
            <a href="/legal/cookies">Política de Cookies</a> y el{" "}
            <a href="/legal/aviso-legal">Aviso Legal</a> de ZEDRO Academy.
          </>
        ),
      },
    ],
  },
  fr: {
    title: "Avis de non-responsabilité",
    updated: "Dernière mise à jour : juin 2026",
    intro: (
      <>
        Les contenus, systèmes, méthodologies, documents, outils et ressources
        offerts sur <strong>ZEDRO Academy</strong> sont fournis à des fins de
        formation, d’information et d’application personnelle.
      </>
    ),
    paragraphs: [
      <>
        ZEDRO Academy est un écosystème formatif numérique conçu pour
        accompagner des processus d’ordre, de clarté, de sens, de réflexion
        personnelle et d’application pratique. Son contenu ne doit pas être
        interprété comme un conseil professionnel personnalisé.
      </>,
    ],
    sections: [
      {
        title: "1. Ne constitue pas un conseil professionnel",
        content: (
          <>
            Rien dans ZEDRO Academy ne constitue un conseil juridique, médical,
            psychologique, thérapeutique, financier, comptable, fiscal, pastoral
            ou professionnel personnalisé. Si vous avez besoin d’une orientation
            précise concernant une situation personnelle, de santé, émotionnelle,
            financière, juridique ou autre, vous devez consulter un professionnel
            qualifié.
          </>
        ),
      },
      {
        title: "2. Usage personnel et responsabilité de l’utilisateur",
        content: (
          <>
            L’utilisation des contenus, exercices, réflexions, outils, carnets,
            ressources ou recommandations générales se fait sous la
            responsabilité de l’utilisateur. Chaque personne doit évaluer son
            contexte, ses limites, ses besoins et ses circonstances avant
            d’appliquer tout contenu de la plateforme.
          </>
        ),
      },
      {
        title: "3. Absence de garantie de résultat",
        content: (
          <>
            ZEDRO Academy ne garantit aucun résultat précis, aucune
            transformation personnelle déterminée ni aucun changement spirituel,
            émotionnel, relationnel, professionnel, financier ou lié à la santé.
            Les résultats dépendent de nombreux facteurs individuels, de
            décisions personnelles, de la constance, du contexte et de
            circonstances externes que ZEDRO Academy ne contrôle pas.
          </>
        ),
      },
      {
        title: "4. Contenu formatif et spirituel",
        content: (
          <>
            Certains contenus peuvent inclure des principes d’inspiration
            judéo-chrétienne, de réflexion spirituelle, de formation du
            caractère, de sens, d’intendance, de relations et de vie quotidienne.
            Ces contenus sont offerts à des fins formatives et ne remplacent pas
            un accompagnement pastoral, un conseil professionnel, des soins
            cliniques ou une intervention spécialisée lorsque cela est
            nécessaire.
          </>
        ),
      },
      {
        title: "5. Santé physique, mentale et émotionnelle",
        content: (
          <>
            L’information liée aux habitudes, au bien-être, à l’ordre personnel,
            aux émotions, au repos, à l’alimentation, au corps, à la discipline
            ou à la vie quotidienne est générale et éducative. Elle ne doit pas
            être utilisée pour diagnostiquer, traiter, prévenir ni remplacer des
            soins médicaux, psychologiques, psychiatriques, nutritionnels ou
            thérapeutiques professionnels.
          </>
        ),
      },
      {
        title: "6. Disponibilité de la plateforme",
        content: (
          <>
            Bien que ZEDRO Academy s’efforce de maintenir la plateforme
            disponible, sécuritaire et fonctionnelle, elle ne garantit pas que
            le service sera exempt d’interruptions, d’erreurs techniques, de
            défaillances temporaires, de pertes d’accès, de maintenance, de
            mises à jour ou d’événements indépendants de sa volonté.
          </>
        ),
      },
      {
        title: "7. Services, liens et intégrations de tiers",
        content: (
          <>
            ZEDRO Academy peut utiliser des services de tiers ou y renvoyer,
            notamment Stripe pour les paiements, des services de courriel, des
            outils d’analyse, d’hébergement, d’infrastructure technologique ou
            d’autres fournisseurs. L’utilisation de ces services peut être
            soumise à leurs propres conditions, politiques et pratiques. ZEDRO
            Academy ne contrôle pas les plateformes externes et n’en assume pas
            la responsabilité.
          </>
        ),
      },
      {
        title: "8. Limitation de responsabilité",
        content: (
          <>
            Dans la mesure permise par la loi applicable, ZEDRO Academy ne sera
            pas responsable des dommages indirects, accessoires ou consécutifs,
            de la perte de données, de la perte d’occasions, des interruptions,
            des décisions personnelles prises par l’utilisateur ni des résultats
            découlant de l’utilisation ou de l’impossibilité d’utiliser la
            plateforme, sauf si la loi applicable l’exige autrement.
          </>
        ),
      },
      {
        title: "9. Lien avec les autres documents juridiques",
        content: (
          <>
            Le présent avis complète les{" "}
            <a href="/legal/terminos">Conditions générales</a>, la{" "}
            <a href="/legal/privacidad">Politique de confidentialité</a>, la{" "}
            <a href="/legal/cookies">Politique de cookies</a> et l’{" "}
            <a href="/legal/aviso-legal">Avis légal</a> de ZEDRO Academy.
          </>
        ),
      },
    ],
  },
};

export default function Disclaimer() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = DISCLAIMER_COPY[language] || DISCLAIMER_COPY.es;

  return (
    <main
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p
        style={{
          color: "rgba(44, 59, 81, 0.72)",
          fontSize: "0.95rem",
          marginTop: "-0.4rem",
        }}
      >
        {copy.updated}
      </p>

      <p>{copy.intro}</p>

      {copy.paragraphs.map((paragraph, index) => (
        <p key={`paragraph-${index}`}>{paragraph}</p>
      ))}

      {copy.sections.map((section) => (
        <section key={section.title} style={{ marginTop: "1.5rem" }}>
          <h2
            style={{
              fontSize: "1.2rem",
              marginBottom: "0.45rem",
              color: "#102b43",
            }}
          >
            {section.title}
          </h2>

          <p>{section.content}</p>
        </section>
      ))}
    </main>
  );
}